import Button from "@mui/material/Button";
import "./allShortageEntry.css";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import AccordionDetails from "@mui/material/AccordionDetails";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TextField from "@mui/material/TextField";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormLabel from "@mui/material/FormLabel";
import FormGroup from "@mui/material/FormGroup";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import Autocomplete from "@mui/material/Autocomplete";
import {
  InitAllShortageFormValue,
  ShortageEntryNBSchema,
} from "../../../schema/shortagesEntry";
import AppState from "./../../../AppState";
import { checkPro } from "./../../../API/checkPro";
import Loader from "./../../../components/loader/loader";
import { PopoutNotification } from "./../../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "./../../../AppState/NotificationType";
import { useTerminalMaster } from "../../../@hooks/useTerminalMaster";
import { usePackageMaster } from "../../../@hooks/usePackageMaster";
import {
  CHECK_DIGIT_FIELDS,
  EFormAction,
  EShortageInvestigationStatus,
  EUploadKey,
  FORM_STYLES,
} from "./../../../constants";
import {
  compareObjects,
  handleKeyDown,
  handleNumberMaxLength,
  handlePaste,
  trimStrings,
  userTerminal,
  isPartiallyCleared,
} from "./../../../helpers";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { IShortageEntryDetails } from "../../../models/shortage";
import { isFormValueChanged } from "../../../AppState/isFormValueChanged";
import { ICheckDigitPro } from "../../../models/osd";
import { CreateShortageEntryAPI } from "../../../API/createShortageEntry";
import ShortageInvestigation from "./shortageInvestigation";
import { GetASDetailsAPI } from "../../../API/getASEntryDetailsAPI";
import UploadDocument from "../../../components/uploadDocument/uploadDocument";
import { UpdateASEntryAPI } from "../../../API/updateASEntry";
import { terminalValuesSelector } from "../../../AppState/selectors";
import TooltipWithClickAway from "../../../components/terminalPicker/TooltipWithClickAway";
import { CommodityField } from "../../../components/shared/form";
import PackageField from "../../../components/shared/form/packageField";
import PageHeaderWithBack from "../../../components/PageHeaderWithBack";
import { useDateTimeFormatter } from "../../../@hooks/useDateTimeFormatter";
import { useAccordionExpand } from "../../../@hooks/useAccordionExpand";
import ClearedInstructionDetails from "../../../components/cleared/clearedInstructionDetails";
import { L_GRID_PROPS, M_GRID_PROPS, S_GRID_PROPS, XL_GRID_PROPS } from "../../../constants";
import { useAccountSpecialInstructions } from "../../../@hooks/useAccountSpecialInstructions";
import SpecialInstructionAccountField from "../../../components/specialInstructions/SpecialInstructionAccountField";
import SpecialInstructionsDialog from "../../../components/specialInstructions/SpecialInstructionsDialog";

/**
 * Fields to delete from request params
 */
const DELETE_FIELDS_BASE = [
  "trailerOrBay",
  "origin",
  "destination",
  "shipper",
  "shipperAccount",
  "shipperAddress",
  "shipperCity",
  "shipperState",
  "shipperZip",
  "consignee",
  "consigneeAccount",
  "consigneeAddress",
  "consigneeCity",
  "consigneeState",
  "consigneeZip",
];

/**
 * Prepare request params for API submission
 * Extracted to reduce component cognitive complexity
 */
const prepareShortageRequestParams = (values: any): any => {
  const reqInput = structuredClone(values);
  const deleteFields = [...DELETE_FIELDS_BASE];

  // Handle value range
  const isLowValueRange = ["1", "2"].includes(reqInput["valueRange"]);
  if (isLowValueRange) {
    deleteFields.push("value1k");
  } else {
    reqInput["value1k"] = Number.parseInt(reqInput["value1k"]);
  }

  // Convert numeric fields
  reqInput["proNumber"] = Number.parseInt(reqInput["proNumber"]);
  reqInput["weight"] = Number.parseInt(reqInput["weight"]);
  reqInput["pieces"] = Number.parseInt(reqInput["pieces"]);
  reqInput["lastManifest"] = Number.parseInt(reqInput["lastManifest"]);

  // Handle trailer vs bay
  const isTrailer = values.trailerOrBay === "T";
  if (isTrailer) {
    reqInput["manifest"] = Number.parseInt(reqInput["manifest"]);
    deleteFields.push("bay");
  } else {
    deleteFields.push("trailer", "manifest");
  }

  // Remove unwanted fields
  deleteFields.forEach((f: string) => {
    delete reqInput[f];
  });

  // Normalize fields
  reqInput["commodity"] = reqInput["commodity"]?.trim();
  reqInput["values"] = reqInput["values"]?.toString();
  reqInput["type"] = "AS";

  return trimStrings(reqInput);
};

function AllShortageEntry() {
  const dispatch = useDispatch();
  const params = useParams();
  const exceptionNumber = params?.osdNumber;
  const location = useLocation();
  const returnMisdeliveryOsdNumber = new URLSearchParams(location.search)
    .get("misdeliveryOsdNumber")
    ?.toString()
    .trim();
  const bnfOsdNumber = location.state?.osdNumber;

  // get terminal master from redux state
  let terminalOptions: string[] = useSelector(terminalValuesSelector);
  const { loadTerminals } = useTerminalMaster();
  const { loadPackageOptions } = usePackageMaster();

  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);

  const [formFieldsDisabled, setFormFieldsDisabled] = React.useState<boolean>(
    Boolean(exceptionNumber)
  );

  const [osdNumber, setOsdNumber] = React.useState<number | undefined>(
    params?.osdNumber ? Number(params?.osdNumber) : undefined
  );
  const [osdDetails, setOsdDetails] = React.useState<IShortageEntryDetails>();
  const [updatedDateTime, setUpdatedDateTime] = React.useState<string>("");
  const formatShortageDateTime = useDateTimeFormatter(setUpdatedDateTime);
  const [investigationStatus, setInvestigationStatus] =
    React.useState<number>();
  const [editFormValueChanged, setEditFormValueChanged] =
    React.useState<boolean>(false);

  const packageCodeOptions = useSelector(
    (state: AppState) => state.packageOptionMasterData
  );
  const [entryUser, setEntryUser] = useState<string>("");
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [proCheckDError, setProCheckDError] = React.useState<boolean>(false);
  // get master from API for commodity code and package code
  const [expanded, setExpanded] = React.useState<string[]>(["panel1"]); // handle expand page using "expanded" variable

  // set Initial value of All Shortage entry form
  const [isEditFormValuePatched, setIsEditFormValuePatched] =
    React.useState<string>("");
  const initValue: any = InitAllShortageFormValue;
  initValue["reporting"] = userTerminal();

  const [initialFormValue, setInitialFormValue] =
    React.useState<any>(initValue);

  const isCleared = osdDetails?.clearedInstructionDetails?.cleared;
  const isPartiallyClearedStatus = isPartiallyCleared(osdDetails?.clearedInstructionDetails) && 
    (investigationStatus === EShortageInvestigationStatus.complete);

  const isSaved = (): boolean => {
    return Boolean(osdNumber ?? isSubmitted);
  };

  /**
   * Validate form submission preconditions
   */
  const validateSubmission = (): boolean => {
    const hasEditableChanges = editFormValueChanged || !osdNumber;
    if (proCheckDError) {
      document.getElementsByName("proNumber")[0].focus();
      return false;
    }
    return hasEditableChanges;
  };

  const submitCreate = (reqInput: any, formValues: any) =>
    CreateShortageEntryAPI(reqInput)
      .then((response) => handleCreateSuccess(response, formValues))
      .catch(handleCreateError);

  const submitUpdate = (reqInput: any, formValues: any) => {
    reqInput["username"] = osdDetails?.username ?? entryUser;
    return UpdateASEntryAPI(reqInput, osdNumber)
      .then((response) => handleUpdateSuccess(response, formValues))
      .catch(handleUpdateError);
  };

  /**
   * Handle form submission - delegates to create or update
   */
  const handleFormSubmit = (values: any) => {
    if (!validateSubmission()) return;
    
    setPageLoading(true);
    const reqInput = prepareRequestParams();

    if (!osdNumber) {
      submitCreate(reqInput, values);
      return;
    }

    submitUpdate(reqInput, values);
  };

  /**
   * Handle successful entry update
   */
  const handleUpdateSuccess = (response: any, formValues: any) => {
    setPageLoading(false);
    setIsSubmitted(true);
    setExpanded(["panel1", "panel2"]);
    setFormFieldsDisabled(true);
    viewAllShortageEntry(osdNumber);
    setInitialFormValue(formValues);
    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.SUCCESS,
        `OS&D # ${osdNumber}  has been updated successfully`,
        Date.now().toString()
      )
    );
  };

  /**
   * Handle entry update error
   */
  const handleUpdateError = (err: any) => {
    setPageLoading(false);
    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        typeof err === "string"
          ? err
          : "All shortage entry updating failed",
        Date.now().toString()
      )
    );
  };

  /**
   * Handle successful entry creation
   */
  const handleCreateSuccess = (response: any, formValues: any) => {
    setPageLoading(false);
    setInitialFormValue(formValues);
    setIsSubmitted(true);
    setExpanded(["panel1", "panel2"]);
    setFormFieldsDisabled(true);
    viewAllShortageEntry(response.osdNumber);
    setOsdNumber(response.osdNumber);
    setEntryUser(response.username);
    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.SUCCESS,
        `OS&D # ${response.osdNumber}  has been created successfully`,
        Date.now().toString()
      )
    );
  };

  /**
   * Handle entry creation error
   */
  const handleCreateError = (err: any) => {
    setPageLoading(false);
    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        err ?? "All shortage entry creation failed",
        Date.now().toString()
      )
    );
  };

  // Initialize All shortage entry form using formik and pass validation schema and initial values
  // handle all form event and submit form using formik

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    handleSubmit,
    setFieldValue,
    resetForm,
    isSubmitting,
  } = useFormik({
    validationSchema: ShortageEntryNBSchema,
    validateOnChange: false,
    initialValues: initValue,

    // Submit the form if all form fields are validated
    onSubmit: handleFormSubmit,
  });

  const hasErrors = (fieldKey: string): boolean => {
    return Boolean(errors[fieldKey] && touched[fieldKey]);
  };

  const specialInstructions = useAccountSpecialInstructions({
    shipperAccount: values.shipperAccount,
    consigneeAccount: values.consigneeAccount,
  });


  const prepareRequestParams = () => prepareShortageRequestParams(values);

  // get Commodity and package code option master
  const getMasters = (ParamString: string) => {
    if (ParamString === "packageCode") {
      loadPackageOptions();
    } else if (ParamString === "terminalMaster") {
      loadTerminals();
    }
  };

  const enableEditing = () => {
    setFormFieldsDisabled(false);
  };

  // validate pro check digit via API if get pro details proD is valid otherwise not valid and show validation message
  const validateProCheckD = async () => {
    if (!values.proNumber) return;

    const pro = get(values, "proNumber", "").toString().padStart(11, "0");
    try {
      const checkProResponse = await checkPro(pro);
      if (isEmpty(checkProResponse)) {
        setProCheckDError(true);
      } else {
        setProCheckDError(false);
      }

      patchProDetails(checkProResponse);
    } catch (error) {
      console.error("Error validating PRO number:", error);
      patchProDetails({});
      setProCheckDError(true);
    }
  };

  // patch pro details value with OV type got the values from proNumber API as above validateProCheckD method
  const patchProDetails = (checkProResponse: ICheckDigitPro) => {
    const fields: Array<keyof ICheckDigitPro> = CHECK_DIGIT_FIELDS;

    fields.forEach((f: keyof ICheckDigitPro) => {
      setFieldValue(
        f,
         checkProResponse[f] ?? (typeof checkProResponse[f] === "string" ? "" : undefined)
      );
    });
  };

  // set form value on exception type changed
  const reset = (action: EFormAction) => {
    setProCheckDError(false);
    document.getElementsByName("reporting")[0].focus();
    resetForm();
    if (action !== EFormAction.create) {
      resetForm();
      patchEditFormValues(initialFormValue);
    }
  };

  // Handle accordion expand (now using custom hook)
  const handleAccordionChange = useAccordionExpand({
    osdNumber,
    expanded,
    setExpanded,
    setFormFieldsDisabled,
    reset,
  });

  const patchEditFormValues = (allShortage: any) => {
    const fields = Object.keys(InitAllShortageFormValue);
    fields.forEach((field: string) => {
      const val =
        typeof allShortage[field] === "string"
          ? allShortage[field]?.trim() || ""
          : allShortage[field];
      setFieldValue(field, val || "");
    });

    setIsEditFormValuePatched(Date.now().toString());
  };

  const viewAllShortageEntry = (osdId?: number | string) => {
    const osdEntryNumber =
      osdId ?? bnfOsdNumber ?? exceptionNumber ?? osdNumber;
    if (osdEntryNumber) {
      // get AS entry and set form fields
      setPageLoading(true);
      GetASDetailsAPI(osdEntryNumber)
        .then((allShortage: IShortageEntryDetails) => {
          setOsdDetails(allShortage);
          setInvestigationStatus(
            allShortage?.investigationStatus ??
              EShortageInvestigationStatus.exceptionEntry
          );
          patchEditFormValues(allShortage);
          formatShortageDateTime(allShortage);
          setPageLoading(false);
        })
        .catch((err: any) => {
          console.error(err);
          dispatch(
            PopoutNotification(
              NOTIFICATION_TYPE.ERROR,
              typeof err === "string" ? err : `OS&D # ${osdNumber} not found`,
              Date.now().toString(),
            ),
          );
          setPageLoading(false);
        });
    }
  };
  const getErrorMessage = (
    error: string,
    touched: boolean,
    className = "form-error"
  ) => {
    if (!error || !touched) return null;
    return <span className={className}>{error}</span>;
  };

  const renderFormErrorMessage = (
    filedName: string,
    className = "form-error"
  ) => {
    if (hasErrors(filedName))
      return <span className={className}>{errors[filedName] as string}</span>;
    return null;
  };

  const renderNumberInputFeild = (
    feildName: string,
    label: string,
    placeholder: string,
    maxLength: number,
    strike: string = "",
    md: number = 3
  ) => {
    return (
      <Grid item md={md} xs={12}>
        <FormControl fullWidth size="small">
          {label && (
            <InputLabel style={FORM_STYLES.LABEL} shrink={true}>
              {label} <sup>{strike}</sup>
            </InputLabel>
          )}
          <TextField
            autoComplete="off"
            id={feildName}
            type="number"
            onPaste={handlePaste}
            placeholder={placeholder}
            variant="outlined"
            name={feildName}
            value={values[feildName]}
            onChange={handleChange}
            error={hasErrors(feildName)}
            disabled={formFieldsDisabled}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleNumberMaxLength(e, maxLength);
            }}
          />
        </FormControl>
        {renderFormErrorMessage(feildName)}
      </Grid>
    );
  };

  const renderDisabledField = (
    fieldName: string,
    label: string,
    md: number = 3
  ) => {
    return (
      <Grid item md={md} xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel style={FORM_STYLES.LABEL} shrink={true} htmlFor={fieldName}>
            {label} <sup>&nbsp;</sup>
          </InputLabel>
          <TextField
            autoComplete="off"
            id={fieldName}
            key={fieldName}
            value={values[fieldName]}
            name={fieldName}
            disabled
            variant="outlined"
          />
        </FormControl>
      </Grid>
    );
  };

  /**
   * Render trailer input fields when trailer option is selected
   */
  const renderTrailerInputs = () => {
    if (values.trailerOrBay !== "T") return null;
    
    return (
      <FormGroup className="form-validation">
        <Grid container spacing={2} className="mb-0">
          <Grid item {...M_GRID_PROPS} className="pt-0">
            <TextField
              autoComplete="off"
              className="justify-content"
              id="outlined-basic"
              placeholder="Enter Trailer No."
              variant="outlined"
              name="trailer"
              key={values.trailerOrBay}
              onChange={handleChange}
              error={hasErrors("trailer")}
              disabled={formFieldsDisabled}
              onBlur={handleBlur}
              value={values.trailer}
              inputProps={{ maxLength: 15 }}
            />
            {values.trailerOrBay === "T" && (
              <>
                <br />
                <br />
                {getErrorMessage(
                  errors.trailer as string,
                  touched.trailer as boolean,
                  "form-error trailer",
                )}
              </>
            )}
          </Grid>
          <Grid item {...M_GRID_PROPS} className="pt-0">
            <TextField
              autoComplete="off"
              id="manifest-number"
              type="number"
              onPaste={handlePaste}
              placeholder="Enter Manifest No."
              variant="outlined"
              name="manifest"
              value={values.manifest}
              onChange={handleChange}
              error={hasErrors("manifest")}
              disabled={formFieldsDisabled}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleNumberMaxLength(e, 9);
              }}
            />
            {values.trailerOrBay === "T" && (
              <>
                <br />
                <br />
                {getErrorMessage(
                  errors.manifest as string,
                  touched.manifest as boolean,
                  "form-error manifest",
                )}
              </>
            )}
          </Grid>
        </Grid>
      </FormGroup>
    );
  };

  /**
   * Render bay input field when bay option is selected
   */
  const renderBayInput = () => {
    if (values.trailerOrBay !== "B") return null;

    return (
      <>
        <FormGroup className="form-validation">
          <Grid container spacing={2} className="mb-0">
            <Grid item {...M_GRID_PROPS} className="pt-0">
              <TextField
                autoComplete="off"
                className="justify-content"
                id="outlined-basic"
                placeholder="Enter Bay/Dock No."
                variant="outlined"
                onChange={handleChange}
                error={hasErrors("bay")}
                disabled={formFieldsDisabled}
                onBlur={handleBlur}
                name="bay"
                value={values.bay}
                inputProps={{ maxLength: 4 }}
              />
            </Grid>
          </Grid>
        </FormGroup>
        {getErrorMessage(
          errors.bay as string,
          touched.bay as boolean,
        )}
      </>
    );
  };

  /**
   * Render form action buttons based on current state
   */
  const renderFormButtons = () => {
    if (isCleared) return null;

    const isEditMode = Boolean(osdNumber) && !formFieldsDisabled;
    const isCreateMode = !osdNumber && !isSubmitted;
    const canEdit = Boolean(osdNumber) && formFieldsDisabled && 
      investigationStatus !== EShortageInvestigationStatus.complete;

    if (isEditMode) {
      return (
        <div className="btn-group-submit">
          <Button
            type="button"
            variant="contained"
            className="clear-btn mr-0 ml"
            onClick={() => reset(EFormAction.edit)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            className={
              editFormValueChanged
                ? "assign mr-0 ml"
                : "disable-btn disable assign mr-0 ml"
            }
          >
            Update
          </Button>
        </div>
      );
    }

    if (isCreateMode) {
      return (
        <div className="btn-group-submit">
          <Button
            type="button"
            variant="contained"
            className="clear-btn mr-0 ml"
            onClick={() => reset(EFormAction.create)}
          >
            Clear
          </Button>
          <Button
            type="submit"
            variant="contained"
            data-testid="submit-button"
            className={
              isSubmitted
                ? "disable-btn disable entry ml"
                : "entry ml"
            }
          >
            {values.valueRange === "3"
              ? "Submit & Send APB"
              : "Submit"}
          </Button>
        </div>
      );
    }

    if (canEdit) {
      return (
        <div className="btn-group-submit">
          <Button
            variant="contained"
            className="assign mr-0 ml"
            onClick={enableEditing}
          >
            Edit
          </Button>
        </div>
      );
    }

    return null;
  };

  // Reusable text field renderer
  const renderTextField = (
    fieldName: string,
    label: string,
    placeholder: string,
    options?: {
      maxLength?: number;
      md?: number;
      required?: boolean;
      errorClass?: string;
    }
  ) => {
    const {
      maxLength = 75,
      md = 3,
      required = true,
      errorClass = "form-error"
    } = options ?? {};

    return (
      <Grid item md={md} xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel
            style={FORM_STYLES.LABEL}
            shrink={true}
            id={`${fieldName}Label`}
          >
            {label} <sup>{required ? "*" : "\u00A0"}</sup>
          </InputLabel>
          <TextField
            autoComplete="off"
            id={fieldName}
            placeholder={placeholder}
            variant="outlined"
            onChange={handleChange}
            error={hasErrors(fieldName)}
            disabled={formFieldsDisabled}
            onBlur={handleBlur}
            name={fieldName}
            value={values[fieldName]}
            inputProps={{ maxLength }}
          />
        </FormControl>
        {getErrorMessage(
          errors[fieldName] as string,
          touched[fieldName] as boolean,
          errorClass
        )}
      </Grid>
    );
  };

  useEffect(() => {
    setFieldValue("commodity", osdDetails?.commodity ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osdDetails]);

  useEffect(() => {
    if (exceptionNumber && isEditFormValuePatched) {
      setInitialFormValue(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditFormValuePatched]);

  useEffect(() => {
    setEditFormValueChanged(!compareObjects(values, initialFormValue));
    dispatch(isFormValueChanged(!compareObjects(values, initialFormValue)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, initialFormValue]);

  /**
   * Load initial master data on mount
   */
  const loadMasterData = () => {
    if (!packageCodeOptions?.length) getMasters("packageCode");
    if (!terminalOptions?.length) getMasters("terminalMaster");
  };

  useEffect(() => {
    dispatch(isFormValueChanged(false));
    if (exceptionNumber || bnfOsdNumber) viewAllShortageEntry();
    loadMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Focus on first error field after submission attempt
   */
  const focusFirstErrorField = () => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      document.getElementsByName(errorKeys[0])[0]?.focus();
    }
  };

  useEffect(() => {
    if (!isSubmitting) focusFirstErrorField();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);

  return (
    <div className="bg-layout overage-entry">
      {/* header */}
      <PageHeaderWithBack
        osdNumber={osdNumber}
        proNumber={osdDetails?.proNumber}
        updatedDateTime={updatedDateTime}
        headingTitle="Shortage Entry"
        msdOsdNumber={returnMisdeliveryOsdNumber}
      />

      {/* body */}
      <div className="content-area">
        <Accordion
          className="accordion"
          expanded={expanded.includes("panel1")}
          onChange={handleAccordionChange("panel1")}
        >
          <AccordionSummary
            className="header"
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography
              className={
                isSaved() ? "queue-check queue-check-gc" : "queue-check"
              }
            >
              1
            </Typography>
            <Typography className="accordion-header">Shortage Entry</Typography>
          </AccordionSummary>
          <form action="" onSubmit={handleSubmit}>
            <AccordionDetails className="accordion-container">
              <div id="overageEntry">
                <Grid container spacing={2} className="filter-margin">
                  <Grid item {...S_GRID_PROPS}>
                    <FormControl fullWidth size="small">
                      <InputLabel
                        style={FORM_STYLES.LABEL}
                        shrink={true}
                        id="TypeLabel"
                      >
                        Type <sup>&nbsp;</sup>
                      </InputLabel>
                      <TextField
                        autoComplete="off"
                        id="type"
                        value="AS"
                        name="origin"
                        disabled
                        variant="outlined"
                      />
                    </FormControl>
                  </Grid>

                  <Grid item {...S_GRID_PROPS}>
                    <FormControl fullWidth size="small">
                      <InputLabel
                        style={FORM_STYLES.LABEL}
                        shrink={true}
                        id="reporting-terminalLabel"
                      >
                        Reporting Terminal <sup>*</sup>
                      </InputLabel>
                      <Autocomplete
                        disablePortal
                        disabled={isSaved()}
                        className="select custom-autocomplete"
                        id="reporting-terminal"
                        size="small"
                        disableClearable={true}
                        value={values?.reporting}
                        options={terminalOptions}
                        getOptionLabel={(option) => option ?? ""}
                        onChange={(e, value) =>
                          setFieldValue("reporting", value)
                        }
                        noOptionsText={"Records not found"}
                        style={FORM_STYLES.LABEL}
                        popupIcon={
                          <div className="dropdown-icon-group">
                            <ArrowDropUpIcon className="icon-style" />{" "}
                            <ArrowDropDownIcon className="icon-style" />{" "}
                          </div>
                        }
                        renderInput={(params) => (
                          <TextField
                            autoComplete="off"
                            {...params}
                            placeholder="Select Reporting Terminal"
                            variant="outlined"
                            className="dropdown-list"
                            name="reporting"
                            style={FORM_STYLES.LABEL}
                            fullWidth
                            inputProps={{
                              ...params.inputProps,
                              style: { fontSize: "0.9rem" },
                            }}
                          />
                        )}
                      />
                    </FormControl>{" "}
                  </Grid>
                </Grid>
                <div>
                  <Grid container spacing={2} className="filter-margin">
                    <Grid item {...S_GRID_PROPS}>
                      <FormControl fullWidth size="small">
                        <InputLabel
                          style={FORM_STYLES.LABEL}
                          shrink={true}
                          id="checkDigitLabel"
                        >
                          PRO# <sup>*</sup>
                        </InputLabel>
                        <TextField
                          autoComplete="off"
                          id="checkDigit"
                          placeholder="Enter Pro Number"
                          variant="outlined"
                          onChange={handleChange}
                          error={Boolean(
                            hasErrors("proNumber") ||
                            proCheckDError /* NOSONAR */,
                          )}
                          disabled={formFieldsDisabled}
                          onBlur={(e) => {
                            validateProCheckD();
                            handleBlur(e);
                          }}
                          value={values.proNumber}
                          name="proNumber"
                          type="number"
                          onPaste={handlePaste}
                          onKeyDown={handleKeyDown}
                          onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                            handleNumberMaxLength(e, 11);
                          }}
                        />
                      </FormControl>
                      {getErrorMessage(
                        errors.proNumber as string,
                        touched.proNumber as boolean,
                      )}

                      {!(errors.proNumber && touched.proNumber) &&
                      proCheckDError ? (
                        <span className="form-error">
                          Enter valid PRO number
                        </span>
                      ) : null}
                    </Grid>
                    {renderDisabledField("origin", "Origin Terminal")}
                    {renderDisabledField(
                      "destination",
                      " Destination Terminal",
                    )}
                  </Grid>

                  <Grid container spacing={2} className="filter-margin">
                    <SpecialInstructionAccountField
                      id="shipperAccount"
                      label="Shipper Account #"
                      value={values.shipperAccount}
                      showIcon={specialInstructions.hasShipperSpecialInstructions}
                      onIconClick={() =>
                        specialInstructions.openSpecialInstructions("shipper")
                      }
                      iconTestId="shipper-special-instructions-icon"
                    />
                    {renderDisabledField("shipper", "Shipper Name")}
                    {renderDisabledField(
                      "shipperAddress",
                      "Shipper Address",
                      6,
                    )}
                  </Grid>
                  <Grid container spacing={2} className="filter-margin">
                    {renderDisabledField("shipperZip", " Shipper Zip")}
                    {renderDisabledField("shipperCity", " Shipper City")}
                    {renderDisabledField("shipperState", "Shipper State")}
                  </Grid>
                  <Grid container spacing={2} className="filter-margin">
                    <SpecialInstructionAccountField
                      id="consigneeAccount"
                      label="Consignee Account #"
                      value={values.consigneeAccount}
                      showIcon={specialInstructions.hasConsigneeSpecialInstructions}
                      onIconClick={() =>
                        specialInstructions.openSpecialInstructions("consignee")
                      }
                      iconTestId="consignee-special-instructions-icon"
                    />
                    {renderDisabledField("consignee", "Consignee Name")}
                    {renderDisabledField(
                      "consigneeAddress",
                      "Consignee Address",
                      6,
                    )}
                  </Grid>

                  <Grid container spacing={2} className="filter-margin">
                    {renderDisabledField("consigneeZip", " Consignee Zip")}
                    {renderDisabledField("consigneeCity", " Consignee City")}
                    {renderDisabledField("consigneeState", "Consignee State")}
                  </Grid>
                </div>

                <Grid container spacing={2} className="filter-margin">
                  {renderNumberInputFeild(
                    "lastManifest",
                    "Last Manifest Number #",
                    "Enter Last Manifest Number #",
                    9,
                    "*",
                  )}

                  <Grid item {...L_GRID_PROPS}>
                    <FormControl className="min-width checkbox-group">
                      <FormLabel id="shipmentLocation">
                        Noted Off <sup>*</sup>
                      </FormLabel>
                      <RadioGroup className="form-row">
                        <Grid container spacing={2} className="mb-0">
                          <Grid item {...M_GRID_PROPS} className="p10 mb-0">
                            <Grid container spacing={2} className="mb-0">
                              <Grid item md={4} xs={12} className="p10 pt-5">
                                <FormControlLabel
                                  value="T"
                                  control={<Radio />}
                                  label="Trailer"
                                  name="trailerOrBay"
                                  onChange={handleChange}
                                  disabled={formFieldsDisabled}
                                  onBlur={handleBlur}
                                  className="full-width"
                                  checked={values.trailerOrBay === "T"}
                                />
                              </Grid>
                              <Grid item md={8} xs={12} className="p10 pt-5">
                                {renderTrailerInputs()}
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item {...M_GRID_PROPS} className="p10 mb-0">
                            <Grid container spacing={2} className="mb-0">
                              <Grid item md={4} xs={12} className="p10 pt-5">
                                <FormControlLabel
                                  value="B"
                                  control={<Radio />}
                                  label="Bay/Dock"
                                  name="trailerOrBay"
                                  onChange={handleChange}
                                  disabled={formFieldsDisabled}
                                  onBlur={handleBlur}
                                  checked={values.trailerOrBay === "B"}
                                  className="full-width"
                                />
                              </Grid>
                              <Grid item md={8} xs={12} className="p10 pt-5">
                                {renderBayInput()}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </RadioGroup>
                    </FormControl>

                    {getErrorMessage(
                      errors.trailerOrBay as string,
                      touched.trailerOrBay as boolean,
                      "form-error checkbox-group-validation",
                    )}
                  </Grid>
                </Grid>

                <Grid container spacing={2} className="filter-margin">
                  <CommodityField
                    id="commodity"
                    label="Commodity"
                    key={values.commodity}
                    disabled={formFieldsDisabled}
                    value={values.commodity}
                    onChange={(_, value) =>
                      setFieldValue("commodity", value?.code ?? "")
                    }
                    placeholder="Enter commodity"
                    required
                    onBlur={handleBlur("commodity")}
                    error={touched.commodity && !!errors.commodity}
                    helperText={
                      (hasErrors("commodity") &&
                      !values.commodity
                        ? errors["commodity"]
                        : undefined) as string | undefined
                    }
                  />

                  <PackageField
                    formFieldsDisabled={formFieldsDisabled}
                    value={values?.package ?? ""}
                    handleChange={(event) => {
                      setFieldValue("package", event.target.value ?? "");
                    }}
                    handleBlur={handleBlur("package")}
                    error={ hasErrors("package") && !values.package
                        ? (errors.package as string)
                        : ""
                    }
                    hasError={(field, condition) =>
                      !!touched[field] && condition
                    }
                  />

                  {renderNumberInputFeild(
                    "pieces",
                    "Total Pieces",
                    "Enter Pieces",
                    5,
                    "*",
                  )}
                  {renderNumberInputFeild(
                    "weight",
                    "Weight",
                    "Enter Weight",
                    7,
                    "*",
                  )}
                </Grid>

                <Grid container spacing={2} className="filter-margin">
                  {renderTextField(
                    "itemCartonModelSerialNumber",
                    "Does the shipment have (Item#, Carton#, Model#, Serial#)",
                    "Enter Item#, Carton#, Model#, Serial# with comma separated",
                    { maxLength: 56, md: 4 },
                  )}
                  <Grid item md={8} xs={12}>
                    <FormControl className="radio-group value-range">
                      <FormLabel id="valueRangeLabel">
                        Value Range <sup>*</sup>
                      </FormLabel>

                      <Grid container spacing={2}>
                        <Grid
                          item
                          {...XL_GRID_PROPS}
                          className="float-div form-content-start"
                        >
                          <FormGroup className="shift-left">
                            <RadioGroup
                              row
                              aria-labelledby="valueRange"
                              name="valueRange"
                              value={values.valueRange}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <FormControlLabel
                                value="1"
                                disabled={isSaved()}
                                control={<Radio />}
                                label="$0 - $250.00"
                                name="valueRange"
                                onChange={handleChange}
                                checked={values.valueRange === "1"}
                              />
                              <FormControlLabel
                                value="2"
                                disabled={isSaved()}
                                control={<Radio />}
                                label="$250.00 - $1,000.00"
                                name="valueRange"
                                onChange={handleChange}
                                checked={values.valueRange === "2"}
                              />
                              <FormControlLabel
                                disabled={isSaved()}
                                value="3"
                                control={<Radio />}
                                label="Greater than $1,000.00"
                                name="valueRange"
                                onChange={handleChange}
                                checked={values.valueRange === "3"}
                              />
                              <TooltipWithClickAway
                                tooltipText="Round the value up to the nearest dollar amount"
                                tooltipKey="tooltip1"
                              />
                            </RadioGroup>
                          </FormGroup>

                          {values.valueRange === "3" && (
                            <FormGroup className="no-wrap">
                              <FormGroup className="form-row shift-left">
                                <TextField
                                  autoComplete="off"
                                  className="justify-content disabled"
                                  value="$"
                                  variant="outlined"
                                  disabled
                                />
                                <TextField
                                  autoComplete="off"
                                  type="number"
                                  onPaste={handlePaste}
                                  className="justify-content"
                                  placeholder="Enter Amount"
                                  variant="outlined"
                                  onChange={handleChange}
                                  error={hasErrors("value1k")}
                                  disabled={isSaved()}
                                  onBlur={handleBlur}
                                  name="value1k"
                                  value={values.value1k}
                                  onKeyDown={handleKeyDown}
                                  onInput={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                  ) => {
                                    handleNumberMaxLength(e, 10);
                                  }}
                                />
                              </FormGroup>
                              {getErrorMessage(
                                errors.value1k as string,
                                touched.value1k as boolean,
                              )}
                            </FormGroup>
                          )}
                        </Grid>
                      </Grid>
                    </FormControl>
                    {getErrorMessage(
                      errors.valueRange as string,
                      touched.valueRange as boolean,
                    )}
                  </Grid>
                </Grid>
                <h3 className="accordion-content-header">Additional Details</h3>

                <Grid container spacing={2} className="filter-margin">
                  {renderTextField(
                    "dimension",
                    "Exact Dimensions/Measurements",
                    "Enter Dimensions/Measurements",
                  )}
                  {renderTextField(
                    "packagingDescription",
                    "Packaging Description",
                    "Packaging  Description",
                  )}
                  {renderTextField(
                    "productDescription",
                    "Product Description",
                    "Enter Product Description",
                  )}
                  {renderTextField(
                    "agentInterline",
                    "Agent/Interline Info And/Or Pro",
                    "Enter Agent/Interline Info And/Or Pro",
                    { required: false },
                  )}
                </Grid>

                <Grid container spacing={2} className="filter-margin">
                  {renderTextField(
                    "additionalDescription",
                    "Description",
                    "Enter Additional Description",
                    { maxLength: 425, md: 12 },
                  )}
                </Grid>
              </div>
              {renderFormButtons()}
            </AccordionDetails>
          </form>
        </Accordion>

        <Accordion
          className="accordion"
          expanded={expanded.includes("panel2")}
          onChange={handleAccordionChange("panel2")}
        >
          <AccordionSummary
            className="header"
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2bh-content"
            id="panel2bh-header"
          >
            <Typography
              className={
                isCleared || investigationStatus === EShortageInvestigationStatus.complete
                  ? "queue-check queue-check-gc"
                  : "queue-check"
              }
            >
              2
            </Typography>
            <Typography className="accordion-header">
              All Short Investigation &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </Typography>
            <Typography className="header-notice note-scroll">
              <div className="animate">
                Do not request camera check prior to reaching that stage of the
                investigation
              </div>
            </Typography>
          </AccordionSummary>

          <ShortageInvestigation
            isFormEditMode={Boolean(exceptionNumber)}
            setInvestigationStatus={setInvestigationStatus}
            osdDetails={osdDetails}
            isCleared={isCleared}
            entryBy={osdDetails?.entryBy}
            assignedTo={osdDetails?.assignedTo}
            assignedBy={osdDetails?.assignedBy}
          />
        </Accordion>
        <Accordion
          className="accordion"
          expanded={expanded.includes("panel3")}
          onChange={handleAccordionChange("panel3")}
        >
          <AccordionSummary
            className="header"
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel3bh-content"
            id="panel3bh-header"
          >
            <Typography className={isCleared? "queue-check queue-check-gc" : "queue-check"}>3</Typography>
            <Typography className="accordion-header">
              {isCleared && !isPartiallyClearedStatus
                ? "Cleared Instruction Details"
                : "Upload Documents"}
            </Typography>
          </AccordionSummary>

          {isCleared && !isPartiallyClearedStatus ? (
            <ClearedInstructionDetails
              clearedInstructionDetails={osdDetails?.clearedInstructionDetails}
            />
          ) : (
            <UploadDocument
              key={expanded.toString() || "upload-document"}
              osdNumber={osdNumber}
              uploadKey={EUploadKey.pro}
            />
          )}
        </Accordion>
        {isPartiallyClearedStatus && (
          <Accordion
            className="accordion"
            expanded={expanded.includes("panel4")}
            onChange={handleAccordionChange("panel4")}
          >
            <AccordionSummary
              className="header"
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel4bh-content"
              id="panel4bh-header"
            >
              <Typography className="queue-check">4</Typography>
              <Typography className="accordion-header">
                Cleared Instruction Details
              </Typography>
            </AccordionSummary>
            <ClearedInstructionDetails
              clearedInstructionDetails={osdDetails?.clearedInstructionDetails}
            />
          </Accordion>
        )}
      </div>
      <Loader pageLoader={pageLoading}></Loader>
      <SpecialInstructionsDialog
        open={specialInstructions.isDialogOpen}
        title={specialInstructions.openedSpecialInstructionsTitle}
        content={specialInstructions.openedSpecialInstructionsText}
        onClose={specialInstructions.closeSpecialInstructions}
      />
    </div>
  );
}

export default AllShortageEntry;
